import React, { useState } from 'react';
import {
  Card,
  CardContent,
  Typography,
  Box,
  Button,
  Chip,
  Divider,
  IconButton,
  Collapse,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions
} from '@mui/material';
import WarningIcon from '@mui/icons-material/Warning';
import HealthAndSafetyIcon from '@mui/icons-material/HealthAndSafety';
import FavoriteIcon from '@mui/icons-material/Favorite';
import OpacityIcon from '@mui/icons-material/Opacity';
import DeviceThermostatIcon from '@mui/icons-material/DeviceThermostat';
import PersonIcon from '@mui/icons-material/Person';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { PhysiologicalRiskEvent } from '../../types/wearable';
import PhysiologicalMonitor from './PhysiologicalMonitor';

interface PhysiologicalAlertCardProps {
  event: PhysiologicalRiskEvent;
  onResolve?: (eventId: string) => void;
}

const PhysiologicalAlertCard: React.FC<PhysiologicalAlertCardProps> = ({
  event,
  onResolve
}) => {
  const [showDetails, setShowDetails] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);

  // 위험 수준에 따른 색상
  const getRiskLevelColor = (level: string) => {
    switch (level.toLowerCase()) {
      case 'high':
        return '#f44336';
      case 'medium':
        return '#ff9800';
      case 'low':
        return '#4caf50';
      default:
        return '#f44336';
    }
  };

  // 위험 원인 한글 표시
  const getRiskCauseLabel = (cause: string) => {
    switch (cause) {
      case 'cardiac':
        return '심장 위험';
      case 'respiratory':
        return '호흡 위험';
      case 'dysrhythmia':
        return '부정맥';
      case 'physiological':
        return '생체 위험';
      case 'motion':
        return '움직임 위험';
      default:
        return cause;
    }
  };

  // 위험 수준 한글 표시
  const getRiskLevelLabel = (level: string) => {
    switch (level.toLowerCase()) {
      case 'high':
        return '고위험';
      case 'medium':
        return '중위험';
      case 'low':
        return '저위험';
      default:
        return level;
    }
  };

  // 이벤트 유형에 따른 제목 표시
  const getEventTitle = () => {
    // 복합 위험인 경우 (motion과 physiological이 모두 있는 경우)
    const isComplex = event.risk_causes.includes('motion') && 
                     (event.risk_causes.includes('physiological') || 
                      event.risk_causes.includes('cardiac') || 
                      event.risk_causes.includes('respiratory') || 
                      event.risk_causes.includes('dysrhythmia'));
    
    if (isComplex) {
      return '복합 위험 감지 (영상 + 생체)';
    }
    
    if (event.risk_causes.includes('motion')) {
      return '영상 기반 위험 감지';
    }
    
    if (event.risk_causes.includes('cardiac')) {
      return '심장 위험 감지';
    }
    
    if (event.risk_causes.includes('respiratory')) {
      return '호흡 위험 감지';
    }
    
    if (event.risk_causes.includes('dysrhythmia')) {
      return '부정맥 위험 감지';
    }
    
    return '생체 신호 위험 감지';
  };

  // 타임스탬프를 읽기 쉬운 형식으로 변환
  const formatTimestamp = (timestamp: string) => {
    const date = new Date(timestamp);
    return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')} ${String(date.getHours()).padStart(2, '0')}:${String(date.getMinutes()).padStart(2, '0')}:${String(date.getSeconds()).padStart(2, '0')}`;
  };

  // 이벤트 해결 처리
  const handleResolveEvent = () => {
    if (onResolve) {
      onResolve(event.id);
    }
    setDialogOpen(false);
  };

  return (
    <>
      <Card 
        sx={{ 
          mb: 2, 
          border: `1px solid ${getRiskLevelColor(event.risk_level)}`,
          boxShadow: 3
        }}
      >
        <CardContent>
          {/* 헤더 영역 */}
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <WarningIcon 
                sx={{ 
                  mr: 1, 
                  color: getRiskLevelColor(event.risk_level) 
                }} 
              />
              <Typography variant="h6">
                {getEventTitle()}
              </Typography>
            </Box>
            <Chip 
              label={getRiskLevelLabel(event.risk_level)} 
              sx={{ 
                bgcolor: getRiskLevelColor(event.risk_level),
                color: 'white', 
                fontWeight: 'bold' 
              }} 
            />
          </Box>
          
          <Divider sx={{ mb: 1 }} />
          
          {/* 기본 정보 */}
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 1 }}>
            {event.risk_causes.map((cause, index) => (
              <Chip 
                key={index}
                label={getRiskCauseLabel(cause)}
                size="small"
                sx={{ 
                  bgcolor: 'rgba(0, 0, 0, 0.08)',
                  '&.MuiChip-root': { height: '24px' } 
                }} 
              />
            ))}
          </Box>
          
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
            <AccessTimeIcon fontSize="small" sx={{ mr: 0.5, color: 'text.secondary' }} />
            <Typography variant="body2" color="text.secondary">
              {formatTimestamp(event.created_at)}
            </Typography>
          </Box>
          
          {event.member_info && (
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <PersonIcon fontSize="small" sx={{ mr: 0.5, color: 'text.secondary' }} />
              <Typography variant="body2" color="text.secondary">
                {event.member_info.name || '알 수 없음'} 
                {event.member_info.age && ` (${event.member_info.age}세)`}
              </Typography>
            </Box>
          )}
          
          {/* 요약 생체 정보 표시 */}
          <Box sx={{ mt: 1, display: 'flex', flexWrap: 'wrap', gap: 1 }}>
            {event.wearable_data?.physio_status?.heart_rate && (
              <Chip
                icon={<FavoriteIcon sx={{ color: '#f44336' }} />}
                label={`심박수: ${event.wearable_data.physio_status.heart_rate.current} BPM`}
                variant="outlined"
                size="small"
              />
            )}
            
            {event.wearable_data?.physio_status?.spo2 && (
              <Chip
                icon={<OpacityIcon sx={{ color: '#2196f3' }} />}
                label={`산소포화도: ${event.wearable_data.physio_status.spo2.current}%`}
                variant="outlined"
                size="small"
              />
            )}
            
            {/* 영상 위험 정보가 있는 경우 표시 */}
            {event.motion_risk && (
              <Chip
                label={`움직임 위험도: ${(event.motion_risk * 10).toFixed(1)}`}
                variant="outlined"
                size="small"
                sx={{ borderColor: '#ff9800', color: '#ff9800' }}
              />
            )}
          </Box>
          
          {/* 상세 정보 토글 버튼 */}
          <Box sx={{ mt: 1, display: 'flex', justifyContent: 'space-between' }}>
            <Button
              variant="outlined"
              size="small"
              onClick={() => setDialogOpen(true)}
              startIcon={<HealthAndSafetyIcon />}
            >
              상세 정보
            </Button>
            
            <IconButton 
              aria-label="details" 
              size="small" 
              onClick={() => setShowDetails(!showDetails)}
            >
              {showDetails ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
            </IconButton>
          </Box>
          
          {/* 접힌 상세 정보 */}
          <Collapse in={showDetails}>
            <Box sx={{ mt: 1 }}>
              <Divider sx={{ my: 1 }} />
              <Typography variant="body2" sx={{ mb: 1, fontWeight: 'bold' }}>
                위험 요약
              </Typography>
              
              <Typography variant="body2">
                {event.risk_causes.includes('cardiac') && 
                  '심장 관련 위험이 감지됨. 심박수가 비정상적인 패턴을 보이고 있음.'}
                {event.risk_causes.includes('respiratory') && 
                  '호흡 관련 위험이 감지됨. 산소포화도가 낮은 상태'}
                {event.risk_causes.includes('dysrhythmia') && 
                  '부정맥 위험이 감지됨. 심박 변이도에 이상 있음'}
              </Typography>
              
              <Box sx={{ mt: 1 }}>
                <Button 
                  variant="contained" 
                  color="success" 
                  fullWidth
                  startIcon={<CheckCircleIcon />}
                  onClick={() => setDialogOpen(true)}
                >
                  이벤트 처리
                </Button>
              </Box>
            </Box>
          </Collapse>
        </CardContent>
      </Card>
      
      {/* 상세 정보 다이얼로그 */}
      <Dialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <WarningIcon 
              sx={{ 
                mr: 1, 
                color: getRiskLevelColor(event.risk_level) 
              }} 
            />
            생체 신호 위험 감지 - 상세 정보
          </Box>
        </DialogTitle>
        <DialogContent dividers>
          <Typography variant="subtitle1" gutterBottom>
            이벤트 ID: {event.id}
          </Typography>
          <Typography variant="subtitle1" gutterBottom>
            감지 시간: {formatTimestamp(event.created_at)}
          </Typography>
          
          {event.member_info && (
            <Typography variant="subtitle1" gutterBottom>
              회원 정보: {event.member_info.name} 
              {event.member_info.age && ` (${event.member_info.age}세)`}
            </Typography>
          )}
          
          <Divider sx={{ my: 2 }} />
          
          {/* 생체 데이터 모니터링 컴포넌트 */}
          <PhysiologicalMonitor 
            wearableId={event.wearable_id}
            physiologicalStatus={event.wearable_data.physio_status}
            wearableRiskData={event.wearable_data}
            memberName={event.member_info?.name}
          />
          
          <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>
            권장 조치
          </Typography>
          
          <Typography variant="body1" paragraph>
            {event.risk_causes.includes('cardiac') && 
              '• 회원 상태 확인. 필요 시 응급 처치 준비\n'}
            {event.risk_causes.includes('respiratory') && 
              '• 호흡 상태 확인. 산소포화도 저하 지속 시 의료 지원 요청\n'}
            {event.risk_causes.includes('dysrhythmia') && 
              '• 심장 리듬 이상 감지. 활동 중단 후 안정\n'}
            • 상태 지속 관찰. 필요 시 응급 의료 호출
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={() => setDialogOpen(false)} 
            color="inherit"
          >
            닫기
          </Button>
          <Button 
            onClick={handleResolveEvent} 
            variant="contained" 
            color="success"
            startIcon={<CheckCircleIcon />}
          >
            이벤트 해결 처리
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default PhysiologicalAlertCard; 